import mixpanel, { type Config, type Mixpanel } from 'mixpanel-browser';
import type { AnalyticsMeta, AnalyticsUser } from '../types';

const isLocalEnv = (): boolean => process.env.NODE_ENV === 'development';

const mixPanelConfig: Partial<Config> = {
  persistence: 'localStorage',
  cross_site_cookie: false,
  cookie_expiration: 7200,
};

export type MixpanelConfigType = {
  projectToken: string;
};

const projects = new Map<string, Mixpanel>();

const getProjectKey = (providerId?: string): string => providerId ?? 'default';

const sendAnalyticsEvent = (
  eventName: string,
  meta?: AnalyticsMeta,
  providerId?: string,
): void => {
  if (isLocalEnv()) return;

  if (providerId) {
    const project = projects.get(providerId);
    if (project) {
      project.track(eventName, meta);
      return;
    }
  }

  projects.forEach((project) => project.track(eventName, meta));
};

const initMixPanel = ({
  config,
  user,
  providerId,
}: {
  config?: MixpanelConfigType;
  user?: AnalyticsUser;
  providerId?: string;
}): void => {
  if (isLocalEnv()) return;
  if (!config?.projectToken) {
    return;
  }

  const key = getProjectKey(providerId);
  const existingProject = projects.get(key);

  if (existingProject) {
    if (user) {
      existingProject.identify(user.id ?? '');
      existingProject.register({
        id: user.id,
        name: user.name ?? '',
        email: user.email ?? '',
        organizationName: user.organizationName ?? '',
        organizationId: user.organizationId ?? '',
        userRole: user.userRole ?? '',
      });
    }
    return;
  }

  const project = mixpanel.init(config.projectToken, mixPanelConfig, key);

  if (user) {
    project.identify(user.id ?? '');
    project.register({
      id: user.id,
      name: user.name ?? '',
      email: user.email ?? '',
      organizationName: user.organizationName ?? '',
      organizationId: user.organizationId ?? '',
      userRole: user.userRole ?? '',
    });
  }

  projects.set(key, project);
};

export { initMixPanel, sendAnalyticsEvent };
